import { ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { TmsLoadsReadiness } from '@numeo/types-main-web-app'
import { Button } from '@/components/ds2'

/** Why the review has no invoicing row for the load: still on the road, no such load, or the read failed (never said as "not delivered"). */
export type LoadAbsence = 'not_delivered' | 'not_found' | 'unread'

/** The reason the readiness answer gives for a load it lists in no invoicing group. A failed read is `unread`, a load it does not name too. */
export const absenceOf = (readiness: TmsLoadsReadiness | undefined, loadNumber: string): LoadAbsence => {
    if (!readiness) return 'unread'
    if (readiness.not_delivered.some((load) => load.load_number === loadNumber)) return 'not_delivered'
    if (readiness.not_found.includes(loadNumber) || readiness.ambiguous.some((entry) => entry.load_number === loadNumber)) return 'not_found'
    return 'unread'
}

/** The board's load page, where a load is marked delivered. */
const loadPageOf = (loadNumber: string): string => `/updater-agent/loads/${encodeURIComponent(loadNumber)}`

interface LoadAbsenceNoteProps {
    loadNumber: string
    absence: LoadAbsence
    onRetry: () => void
}

/**
 * What the review says when the load is not an invoicing row: a load still on the road points to its load page (marked delivered
 * there, it shows up here); a failed read offers to read again, and never claims the load is not delivered.
 */
export const LoadAbsenceNote = ({ loadNumber, absence, onRetry }: LoadAbsenceNoteProps) => {
    if (absence === 'not_delivered')
        return (
            <div className="flex flex-col items-start gap-3">
                <p className="text-sm text-foreground">
                    Load #{loadNumber} is not marked delivered yet, so it cannot be invoiced. Mark it delivered on the load page; it then shows up here.
                </p>
                <Link
                    to={loadPageOf(loadNumber)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-8 items-center gap-2 rounded-3xl bg-accent px-3 text-sm font-medium text-accent-foreground transition-colors duration-150 hover:bg-accent-hover"
                >
                    Open load page
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </Link>
            </div>
        )
    if (absence === 'not_found') return <p className="text-sm text-foreground">No load #{loadNumber} was found. It may have been renamed or removed.</p>
    return (
        <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-foreground">Load #{loadNumber} could not be read right now.</p>
            <Button size="sm" variant="secondary" onClick={onRetry}>
                Try again
            </Button>
        </div>
    )
}
